import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import './index.css';
import './i18n';
import { registerServiceWorker, setupInstallPrompt } from './utils/registerSW';
import { isMobileApp } from './utils/mobileBridge';

// ============================================================================
// Error reporter — Opcija B (dijagnostika iOS WKWebView pucanja)
// ----------------------------------------------------------------------------
// Šalje TAČNU grešku (poruka, fajl, linija, stack) na server gdje nginx
// loguje GET request u /var/log/nginx/client-errors.log. Same-origin GET
// preko fetch(keepalive) — preživljava i ako app pukne odmah nakon error-a.
// Fallback: new Image().src — radi i u izolovanim contextima.
//
// PRAVILO: reporter NE smije sam rušiti app. Sve u try/catch.
// ============================================================================

const REPORTER_ENDPOINT = '/_clientlog';

// Spriječi spam: max 5 izvještaja po session-u, dedupe po (msg+file+line)
let __reportCount = 0;
const __reportedKeys = new Set<string>();
const __MAX_REPORTS = 5;

function __reportError(data: Record<string, unknown>): void {
  try {
    if (__reportCount >= __MAX_REPORTS) return;

    const key = `${data.type}|${data.msg}|${data.file}|${data.line}`;
    if (__reportedKeys.has(key)) return;
    __reportedKeys.add(key);
    __reportCount += 1;

    const params = new URLSearchParams();
    params.set('ts', new Date().toISOString());
    params.set('isMobile', isMobileApp() ? '1' : '0');
    try { params.set('href', String(location.href).slice(0, 500)); } catch (_) {}
    try { params.set('ua', String(navigator.userAgent).slice(0, 300)); } catch (_) {}
    Object.entries(data).forEach(([k, v]) => {
      if (v == null) return;
      const s = typeof v === 'string' ? v : String(v);
      params.set(k, s.slice(0, 1500));
    });

    const url = `${REPORTER_ENDPOINT}?${params.toString()}`;

    let sent = false;
    try {
      void fetch(url, { method: 'GET', keepalive: true, mode: 'no-cors', credentials: 'omit' })
        .catch(() => { /* fallback below */ });
      sent = true;
    } catch (_) { /* fall through to Image fallback */ }

    if (!sent) {
      try {
        const img = new Image();
        img.src = url;
      } catch (_) { /* tiho */ }
    }
  } catch (_) {
    /* nikad ne ruši app zbog reportera */
  }
}

try {
  window.addEventListener('error', (e: ErrorEvent) => {
    __reportError({
      type: 'error',
      msg: e?.message ?? '',
      file: e?.filename ?? '',
      line: e?.lineno ?? '',
      col: e?.colno ?? '',
      stack: (e?.error && e.error.stack) ? String(e.error.stack) : '',
    });
  });

  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    const reason: any = (e as any)?.reason;
    __reportError({
      type: 'unhandledrejection',
      msg: reason?.message ? String(reason.message) : String(reason ?? ''),
      stack: reason?.stack ? String(reason.stack) : '',
    });
  });
} catch (_) {
  /* ne ruši ako addEventListener fail-uje */
}

// ============================================================================

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </React.StrictMode>
);

// PWA setup - registracija SW + install prompt event listener
setupInstallPrompt();
registerServiceWorker();
